import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import {MatchAPI} from "@/api/match";

import AddIcon from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";

import {ERROR_OPERATION, dateFormat} from "@/utils/constants";
import {fetchData} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const dateTimeFormat = "YYYY-MM-DDTHH:mm:ss";

const defaultFiltersData = {
	toDate: moment().endOf("month").format(dateTimeFormat),
};

function ScoutingMatchesPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [matches, setMatches] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("scouting_planning_filters")) ||
			defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"scouting_planning_filters",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
		onlyPro: true,
	});

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-3",
			clearable: true,
			value: filtersData?.fromDate
				? moment(filtersData?.fromDate).format("YYYY-MM-DD")
				: null,
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						fromDate: moment(e)
							.set({hour: 0, minute: 0, second: 0})
							.format(dateTimeFormat),
					});
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-3",
			clearable: false,
			value: moment(filtersData?.toDate).format("YYYY-MM-DD"),
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						toDate: moment(e)
							.set({hour: 23, minute: 59, second: 59})
							.format(dateTimeFormat),
					});
				}
			},
		},
		{
			key: "categoryId",
			type: "select",
			options: categories,
			sortOrder: "descend",
			label: "Categoria",
			customClassName: "col-12 col-sm-3",
			clearable: true,
			value: filtersData?.categoryId,
			setValue: (value) => {
				setFiltersData({...filtersData, categoryId: value});
			},
		},
	];

	const getMatches = () => {
		fetchData(
			MatchAPI.list,
			{
				...filtersData,
				categoryId: filtersData?.categoryId?.value,
				matchObserved: true,
				seasonId,
			},
			() => setLoading(true),
			setMatches,
			() => handleNotification(ERROR_OPERATION, "error"),
			() => setLoading(false),
		);
	};

	useEffect(() => {
		if (categories?.length) {
			getMatches();
		}
	}, [filtersData, categories]);

	let fullFilteredMatches = Array.from(matches);

	// const groupedMatches = {};
	// categories.forEach((c) => {
	// 	const currentCategory = c.label;
	// 	const matchesMembers = fullFilteredMatches?.filter(
	// 		(t) => t.playersCategory === currentCategory,
	// 	);
	// 	groupedMatches[currentCategory] = matchesMembers;
	// });

	const listConfiguration = {
		data: fullFilteredMatches,
		onClick: (match) => navigate("/scouting/planning/" + match.id),
	};

	const columns = [
		{
			label: "Data",
			key: "match.date",
			customRender: (item) => {
				return moment(item.startDate).format(dateFormat + " HH:mm");
			},
			medium: true,
		},
		{
			label: "Luogo",
			key: "fieldName",
			medium: true,
			align: "start",
		},
		{
			label: "Osservatori",
			key: "scoutNames",
			align: "start",
		},
		{
			label: "Partita",
			key: "match.homeClub",
			customRender: (item) => {
				return item.homeClub + " - " + item.awayClub;
			},
			align: "start",
		},
		{
			label: "Categoria",
			key: "match.membershipCategory",
			customRender: (item) => {
				return item.playersCategory;
			},
			small: true,
			align: "start",
		},
		{
			label: "Leva",
			key: "match.membershipCategory",
			customRender: (item) => {
				const categoryNumber = item.playersCategory?.split(" ")?.[1];
				return 2025 - categoryNumber;
			},
			small: true,
			align: "center",
		},
		{
			label: "Obiettivi",
			key: "match.prospectsObserved",
			customRender: (item) => {
				return item?.prospectsObserved?.length;
			},
			small: true,
			align: "center",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBack color="primary" />,
			variant: "outlined",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.subtract(1, "month")
						.format(dateTimeFormat),
					toDate: moment(filtersData?.toDate)
						.subtract(1, "month")
						.format(dateTimeFormat),
				});
			},
		},
		{
			icon: <ArrowForward color="primary" />,
			variant: "outlined",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.add(1, "month")
						.format(dateTimeFormat),
					toDate: moment(filtersData?.toDate)
						.add(1, "month")
						.format(dateTimeFormat),
				});
			},
		},
	];

	mainActions.push({
		icon: <AddIcon />,
		onClick: () => navigate("/scouting/planning/new"),
	});

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs actions={<MainActions actions={mainActions} />} />
			<CardsContainer
				tableComponent={
					<CardsTable
						breadcrumbs
						fullWidth
						filtersData={filters}
						configurationColumns={columns}
						data={fullFilteredMatches}
						handleClick={(item) => {
							navigate("/scouting/planning/" + item.id);
						}}
						defaultSortedField="startDate"
						defaultSortedOrder="descend"
					/>
				}
				listComponent={
					<CardsList
						type="scouting_match"
						breadcrumbs
						// grouped
						filtersData={filters}
						listConfiguration={listConfiguration}
						defaultSortedField="startDate"
						defaultSortedOrder="descend"
					/>
				}
			/>
		</Page>
	);
}

export default ScoutingMatchesPage;
